import { paintTrack } from './trackPainter.js';

/**
 * Exports a track's look as a TILE PYRAMID: the same picture the game used to receive whole, cut
 * into small tiles at several densities, plus a small whole-map fallback. The game draws only the
 * tiles a camera is looking at, at the level closest to the screen's own density (see the game's
 * rendering/TrackTiles.js); this is what makes a track sharp at every zoom without holding a
 * photograph of the whole map in memory.
 *
 * The painting is done in STRIPS, never on one canvas of the whole map: the painter draws in world
 * coordinates on a context the caller has scaled and translated, so a strip of the map painted on
 * its own canvas is pixel-for-pixel the same as the same strip of a full-map painting (verified
 * against a full paint at the same scale). One strip of the biggest tracks is ~65 MB, against the
 * 500+ MB a 2 px/unit canvas of the whole map would need.
 *
 * Output paths are relative to the game's asset root: `tiles/<id>/<level>/<x>_<y>.webp` and
 * `tiles/<id>/base.webp`, with `manifest` going into the track's JSON as `backgroundTiles`.
 *
 * Every tile is baked with a 1-texel GUTTER of the neighbouring content around its visible square,
 * and the game draws only that inner square (a texture frame): two tiles are separate textures, and
 * without the gutter the bilinear filter at the shared edge has no neighbour texel to read, which
 * shows up as a thin line along the seam.
 */
export const TILE_PX = 512;
export const TILE_GUTTER = 1;            // texels of neighbour content baked around each tile
export const TILE_LEVELS = [2, 1, 0.5];   // px per world unit, finest first
export const BASE_SCALE = 0.25;           // px per world unit of the whole-map fallback
// How much of the map is painted per call. 512 world units is one level-1 tile row, and two level-0
// rows; the canvas of a 7900-unit-wide track is ~16 Mpx (65 MB), which a browser handles.
const STRIP_WORLD = 512;
// Extra world units painted above and below a strip: the gutter of the COARSEST level is the widest
// in world units, and it has to find painted content even at a strip boundary.
const STRIP_MARGIN = Math.ceil(TILE_GUTTER / TILE_LEVELS[TILE_LEVELS.length - 1]);

/** The `backgroundTiles` block a track file carries (see the game's TrackTiles). */
export function tileManifest(track) {
  const dir = `tiles/${track.id ?? 'track_custom'}`;
  return {
    dir,
    tilePx: TILE_PX,
    gutter: TILE_GUTTER,
    levels: TILE_LEVELS.slice(),
    base: `${dir}/base.webp`,
  };
}

/**
 * The whole-map thumbnail the track picker shows, beside the tile folders: it is not a tile, it is
 * the one picture a player sees in a list. `PREVIEW_MAX_PX` is the longest side — a card is 130 CSS
 * px tall, so this is crisp at 2x and still a few tens of KB, where the full picture was the map's
 * own resolution.
 */
export const PREVIEW_MAX_PX = 512;

export function tilePreviewPath(track) {
  return `preview/${track.id ?? 'track_custom'}.webp`;
}

/**
 * Paints `track` into tiles.
 *
 * @param {object} track                the track file, as the game reads it
 * @param {object} opts
 * @param {Function} opts.encode        `(canvas, quality) => Promise<Uint8Array|Blob>`, WebP
 * @param {Function} [opts.createCanvas] `(w, h) => canvas`; defaults to document.createElement
 * @param {object} [opts.images]        decoded material sprites, keyed by src (see trackPainter)
 * @param {object} [opts.terrainImages] decoded ground textures, keyed by terrain
 * @param {Function} [opts.onProgress]  `({ phase, index, total })`; may return a promise (a paint is
 *                                      synchronous, so this is where a caller lets the browser repaint)
 * @returns {Promise<{ manifest, files: { path: string, data: Uint8Array }[] }|null>}
 */
export async function exportTrackTiles(track, {
  encode,
  createCanvas = defaultCreateCanvas,
  images = {},
  terrainImages = {},
  onProgress = null,
} = {}) {
  if (!track || !(track?.width > 0) || !(track?.height > 0)) return null;
  if (typeof encode !== 'function') throw new Error('exportTrackTiles: an encode(canvas) function is required');

  const id = track.id ?? 'track_custom';
  const files = [];
  let tileCount = 0;
  const stripCount = Math.ceil(track.height / STRIP_WORLD);

  // The whole map at BASE_SCALE: transparent where the map ends, stretched over the world by the
  // game. It is the fallback under every tile, never a hole while a tile is in flight.
  const base = createCanvas(
    Math.max(1, Math.ceil(track.width * BASE_SCALE)),
    Math.max(1, Math.ceil(track.height * BASE_SCALE)),
  );
  const baseCtx = base.getContext('2d');
  baseCtx.imageSmoothingEnabled = true;
  baseCtx.imageSmoothingQuality = 'high';

  // One accumulator per level: a tile row is `TILE_PX / scale` world units tall, which is not the
  // same as a strip for every level, so a row can span several strips (or several rows fit in one).
  const rows = TILE_LEVELS.map((scale, level) => ({
    level,
    scale,
    rowWorld: TILE_PX / scale,
    canvas: null,
    ctx: null,
    startY: 0,
    filled: 0,
  }));

  const flushRow = async (row) => {
    if (!row.canvas) return;
    const contentWidth = row.canvas.width - TILE_GUTTER * 2;
    const columns = Math.ceil(contentWidth / TILE_PX);
    const rowIndex = Math.round(row.startY / row.rowWorld);
    const outSize = TILE_PX + TILE_GUTTER * 2;
    for (let x = 0; x < columns; x++) {
      const tile = createCanvas(outSize, outSize);
      const ctx = tile.getContext('2d');
      const sx = x * TILE_PX;
      const sw = Math.min(outSize, row.canvas.width - sx);
      const sh = row.canvas.height;
      // The last column/row of the map is short: the rest of the tile stays transparent, and the
      // band painted around the map shows through it exactly as it does on screen.
      ctx.drawImage(row.canvas, sx, 0, sw, sh, 0, 0, sw, sh);
      const data = await encode(tile, 0.9);
      files.push({ path: `tiles/${id}/${row.level}/${x}_${rowIndex}.webp`, data });
      tileCount += 1;
    }
    releaseCanvas(row.canvas);
    row.canvas = null;
    row.ctx = null;
    row.filled = 0;
  };

  const addStripToRow = async (row, strip, stripY, stripHeight) => {
    let localY = 0;
    // A texel of gutter, in world units at this level. Above and below the row it comes from the
    // strip's own painted margin; left and right it is already in the padded row canvas.
    const gutterWorld = TILE_GUTTER / row.scale;
    while (localY < stripHeight - 1e-6) {
      const worldY = stripY + localY;
      const startY = Math.floor(worldY / row.rowWorld) * row.rowWorld;
      if (!row.canvas || row.startY !== startY) {
        if (row.canvas) await flushRow(row);
        row.canvas = createCanvas(
          Math.max(1, Math.ceil(track.width * row.scale)) + TILE_GUTTER * 2,
          TILE_PX + TILE_GUTTER * 2,
        );
        row.ctx = row.canvas.getContext('2d');
        row.ctx.imageSmoothingEnabled = true;
        row.ctx.imageSmoothingQuality = 'high';
        row.startY = startY;
        row.filled = 0;
      }
      const offset = worldY - row.startY;
      const take = Math.min(stripHeight - localY, row.rowWorld - offset);
      // Source rect is in the strip's pixels (2 px/unit, plus STRIP_MARGIN around it); destination
      // is the row's own density. drawImage does the downscale the level asks for.
      row.ctx.drawImage(
        strip,
        0, (localY + STRIP_MARGIN) * 2, strip.width, take * 2,
        TILE_GUTTER, TILE_GUTTER + offset * row.scale, track.width * row.scale, take * row.scale,
      );
      // The row's first and last texel of gutter, read from the painted margin above/below it.
      if (offset <= 1e-6) {
        row.ctx.drawImage(
          strip,
          0, (localY + STRIP_MARGIN - gutterWorld) * 2, strip.width, gutterWorld * 2,
          TILE_GUTTER, 0, track.width * row.scale, TILE_GUTTER,
        );
      }
      if (offset + take >= row.rowWorld - 1e-6) {
        row.ctx.drawImage(
          strip,
          0, (localY + take + STRIP_MARGIN) * 2, strip.width, gutterWorld * 2,
          TILE_GUTTER, TILE_GUTTER + TILE_PX, track.width * row.scale, TILE_GUTTER,
        );
      }
      row.filled += take;
      localY += take;
      if (row.filled >= row.rowWorld - 1e-6) await flushRow(row);
    }
  };

  for (let index = 0; index < stripCount; index++) {
    const stripY = index * STRIP_WORLD;
    const stripHeight = Math.min(STRIP_WORLD, track.height - stripY);
    await onProgress?.({ phase: 'paint', index, total: stripCount });

    const strip = createCanvas(
      Math.max(1, Math.ceil(track.width * 2)),
      Math.max(1, Math.ceil((stripHeight + STRIP_MARGIN * 2) * 2)),
    );
    const ctx = strip.getContext('2d');
    ctx.scale(2, 2);
    ctx.translate(0, -(stripY - STRIP_MARGIN));
    ctx.beginPath();
    ctx.rect(0, stripY - STRIP_MARGIN, track.width, stripHeight + STRIP_MARGIN * 2);
    ctx.clip();
    paintTrack(ctx, track, {
      scale: 2, createCanvas, images, terrainImages,
      bounds: { x: 0, y: stripY - STRIP_MARGIN, width: track.width, height: stripHeight + STRIP_MARGIN * 2 },
    });

    // Only the strip's own band goes into the base: its painted margin belongs to the neighbours.
    baseCtx.drawImage(
      strip,
      0, STRIP_MARGIN * 2, strip.width, stripHeight * 2,
      0, stripY * BASE_SCALE, track.width * BASE_SCALE, stripHeight * BASE_SCALE,
    );
    for (const row of rows) await addStripToRow(row, strip, stripY, stripHeight);
    releaseCanvas(strip);   // ~65 MB: the next strip must not be allocated on top of it
  }
  for (const row of rows) await flushRow(row);

  files.push({ path: `tiles/${id}/base.webp`, data: await encode(base, 0.9) });

  // The card picture: the whole map shrunk to a thumbnail, never enlarged. It is encoded from the
  // base, so a track's list entry costs one small download instead of the map's own picture.
  const previewScale = Math.min(1, PREVIEW_MAX_PX / Math.max(base.width, base.height));
  const preview = createCanvas(
    Math.max(1, Math.round(base.width * previewScale)),
    Math.max(1, Math.round(base.height * previewScale)),
  );
  const previewCtx = preview.getContext('2d');
  previewCtx.imageSmoothingEnabled = true;
  previewCtx.imageSmoothingQuality = 'high';
  previewCtx.drawImage(base, 0, 0, preview.width, preview.height);
  files.push({ path: tilePreviewPath(track), data: await encode(preview, 0.8) });
  releaseCanvas(preview);
  releaseCanvas(base);

  return { manifest: tileManifest(track), files, tileCount };
}

/**
 * Encodes a canvas as WebP. Tiles are fetched from a fixed `.webp` name, so there is no PNG
 * fallback here: a browser that cannot write WebP says so, and the editor's single-picture export
 * (which does fall back) stays available.
 */
export function encodeWebp(canvas, quality = 0.9) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) return reject(new Error('canvas.toBlob returned nothing'));
      if (blob.type !== 'image/webp') return reject(new Error('this browser cannot write WebP'));
      blob.arrayBuffer().then((buffer) => resolve(new Uint8Array(buffer)), reject);
    }, 'image/webp', quality);
  });
}

/**
 * Drops a canvas' backing store now instead of whenever the collector gets to it: during an export
 * several 16 Mpx canvases come and go, and the browser has to have the memory before allocating the
 * next one.
 */
function releaseCanvas(canvas) {
  canvas.width = 1;
  canvas.height = 1;
}

function defaultCreateCanvas(width, height) {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
}
